import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { resolveCoords, INDIA_CENTER } from "@/data/cityCoords";
import { formatINR, imgUrl } from "@/lib/format";

const priceIconHtml = (label, sold) =>
  `<div style="background:${sold ? "#D4AF37" : "#0F3D91"};color:#fff;padding:4px 10px;border-radius:9999px;font-family:'Plus Jakarta Sans',sans-serif;font-size:12px;font-weight:700;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.3);border:2px solid #fff;">${label}</div>`;

export default function PropertyMap({ properties }) {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const withCoords = properties.map((p) => ({ p, pos: resolveCoords(p) }));
    const center = withCoords.length ? withCoords[0].pos : INDIA_CENTER;
    const map = L.map(containerRef.current).setView(center, withCoords.length ? 11 : 5);
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);
    map.on("popupopen", (e) => {
      const btn = e.popup._contentNode?.querySelector("[data-nav]");
      if (btn) btn.addEventListener("click", () => navigate(`/properties/${btn.getAttribute("data-nav")}`));
    });
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) { layerRef.current.remove(); }
    const group = L.layerGroup().addTo(map);
    layerRef.current = group;
    const withCoords = properties.map((p) => ({ p, pos: resolveCoords(p) }));
    withCoords.forEach(({ p, pos }) => {
      const marker = L.marker(pos, {
        icon: L.divIcon({ className: "", html: priceIconHtml(p.sold ? "SOLD" : formatINR(p.price), p.sold), iconSize: [60, 24], iconAnchor: [30, 24] }),
      });
      marker.bindPopup(
        `<div style="width:200px">
          <img src="${imgUrl(p.images?.[0])}" style="width:100%;height:110px;object-fit:cover;border-radius:8px" />
          <p style="margin:8px 0 2px;font-weight:700;color:#0F3D91">${formatINR(p.price)}</p>
          <p style="margin:0;font-size:13px">${p.title}</p>
          <p style="margin:2px 0 8px;font-size:12px;color:#64748b">${p.locality}, ${p.city}</p>
          <button data-nav="${p.id}" style="background:#0F3D91;color:#fff;border:0;border-radius:8px;padding:6px 12px;font-size:12px;font-weight:600;cursor:pointer;width:100%">View details</button>
        </div>`
      );
      group.addLayer(marker);
    });
    if (withCoords.length) {
      map.fitBounds(L.latLngBounds(withCoords.map((w) => w.pos)).pad(0.3), { maxZoom: 12 });
    }
  }, [properties]);

  return <div ref={containerRef} data-testid="property-map" className="rounded-2xl overflow-hidden border border-slate-200" style={{ height: "70vh", zIndex: 0 }} />;
}
